import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CATALOG_PRODUCTS, CATEGORIES, EGG_SUBCATEGORIES } from "../data/products.js";
import { useDisabledCategories } from "../hooks/useDisabledCategories.js";
import ProductCard from "../components/ProductCard.jsx";
import Seo from "../components/Seo.jsx";
import ProductJsonLd from "../components/ProductJsonLd.jsx";

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "price-asc", label: "Price: low to high" },
  { id: "price-desc", label: "Price: high to low" },
  { id: "name", label: "Name A–Z" },
];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const [query, setQuery] = useState(() => params.get("search") || "");
  const [sort, setSort] = useState("featured");
  const disabledCategories = useDisabledCategories();

  // Header search sets ?search= even when already on this page (client-side
  // navigation doesn't remount, so the state init above only fires once) —
  // keep the box in sync with the URL on every param change.
  useEffect(() => {
    const s = params.get("search");
    if (s !== null) setQuery(s);
  }, [params]);

  const visibleCategories = CATEGORIES.filter(
    (c) => c.id === "all" || !disabledCategories?.has(c.id)
  );

  // The Eggs tab has its own sub-menu (Free Range / Conventional) — kept as a
  // separate ?sub= param so it doesn't collide with other categories' state.
  const sub = category === "eggs" ? params.get("sub") || "all" : "all";

  const setCategory = (id) => {
    const next = new URLSearchParams(params);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    next.delete("sub");
    setParams(next);
  };

  const setSub = (id) => {
    const next = new URLSearchParams(params);
    if (id === "all") next.delete("sub");
    else next.set("sub", id);
    setParams(next);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = CATALOG_PRODUCTS.filter((p) => {
      const inCat = category === "all" || p.category === category;
      const inSub = category !== "eggs" || sub === "all" || p.subCategory === sub;
      const inQuery = !q || p.name.toLowerCase().includes(q) || p.tag.toLowerCase().includes(q);
      // The "All" tab hides out-of-stock items; browsing a specific category
      // (e.g. Mangoes) still shows them with their "Out of stock" state.
      const inStock = category !== "all" || !p.soldOut;
      // An admin-hidden category disappears from "All" too, not just its chip —
      // still reachable via its own ?category= link or search.
      const inEnabledCategory = category !== "all" || !disabledCategories?.has(p.category);
      return inCat && inSub && inQuery && inStock && inEnabledCategory;
    });
    if (sort === "price-asc") list = [...list].sort((a, b) => a.pricePerKg - b.pricePerKg);
    else if (sort === "price-desc") list = [...list].sort((a, b) => b.pricePerKg - a.pricePerKg);
    else if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [category, sub, query, sort, disabledCategories]);

  const activeLabel = CATEGORIES.find((c) => c.id === category)?.label || "All products";

  return (
    <>
      <Seo
        title="Shop Farm-Fresh Produce — Mangoes, Eggs, Milk & More"
        description="Browse our farm-fresh range: Banganapalli, Totapuri & Neelam mangoes, free-range & pasteurized eggs, A2 & fresh milk, wild honey and country chicken. Order direct from our Andhra Pradesh farm."
        path="/products"
      />
      <ProductJsonLd />
      <div className="plp-hero">
        <div className="plp-hero__scrim" />
        <div className="container plp-hero__inner">
          <span className="section__tag" style={{ color: "#ffd9b3" }}>Shop</span>
          <h1>{category === "all" ? "All farm-fresh produce" : activeLabel}</h1>
          <p>Farm-direct mangoes, eggs, milk, poultry &amp; honey.</p>
        </div>
      </div>

      <section className="section products-page">
        <div className="container">
          <div className="plp__toolbar">
            <div className="plp__search">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                placeholder="Search eggs, milk, mangoes..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <label className="plp__sort">
              Sort
              <select value={sort} onChange={(e) => setSort(e.target.value)}>
                {SORTS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="chips">
            {visibleCategories.map((c) => (
              <button
                key={c.id}
                className={`chip ${category === c.id ? "chip--active" : ""}`}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {category === "eggs" && (
            <div className="chips chips--sub" role="group" aria-label="Egg type">
              {EGG_SUBCATEGORIES.map((s) => (
                <button
                  key={s.id}
                  className={`chip chip--sm ${sub === s.id ? "chip--active" : ""}`}
                  onClick={() => setSub(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          <p className="plp__count">
            {filtered.length} item{filtered.length !== 1 ? "s" : ""}
            {category !== "all" ? ` in ${activeLabel}` : ""}
          </p>

          {filtered.length ? (
            <div className="grid">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          ) : (
            <p className="plp__empty">No products match your search.</p>
          )}
        </div>
      </section>
    </>
  );
}
